import { updateBreadcrumbs, refreshCartCount, refreshMiniCart, refreshCart } from './utils.js';

window.addEventListener('DOMContentLoaded', async () => {
  const API_BASE = 'https://chesapeake-cart-sync-app-ashy.vercel.app/api';
  const STORE_A = 'j9k5kh-w0.myshopify.com';
  const STORE_B = 'x6pzfi-dv.myshopify.com';
  const originalFetch = window.fetch.bind(window);
  const host = window.Shopify.shop;

  let sessionId = null;
  let syncDebounceTimer = null;
  let origin = null;
  let entryStore = null;
  let entryTimestamp = null;
  let entrySource = null;

  const customerEmail = window.customer?.customer_email || null;

  let cartOperationQueue = [];
  let isProcessingQueue = false;
  let pendingSyncPromise = null;
  const DEBOUNCE_DELAY = 300;
  const MAX_BATCH_SIZE = 5;
  let lastChangeElement = null;

  function toggleItemTotalsSpinnerFromElement(element, show) {
    try {
      const el = element instanceof HTMLElement ? element : null;
      if (!el) return;
      const item = el.closest('.c-cart-item') || el.closest('.cart-item');
      if (!item) return;
      const spinner = item.querySelector('.cart-item__totals .loading__spinner');
      if (!spinner) return;
      if (show) {
        spinner.classList.remove('hidden');
      } else {
        spinner.classList.add('hidden');
      }
    } catch (_) {}
  }

  // ───────────────────────────────────────────────────────────────────────────
  // UI HELPERS (NOTIFICATIONS & SPINNER)
  // ───────────────────────────────────────────────────────────────────────────

  function showPageSpinner() {
    let overlay = document.getElementById('cart-sync-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'cart-sync-overlay';
      Object.assign(overlay.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: '10000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });
      document.body.appendChild(overlay);
    }

    let spinner = document.getElementById('cart-sync-spinner-page');
    if (!spinner) {
      spinner = document.createElement('div');
      spinner.id = 'cart-sync-spinner-page';
      Object.assign(spinner.style, {
        display: 'block',
        width: '50px',
        height: '50px',
        border: '5px solid rgba(255, 255, 255, 0.3)',
        borderTop: '5px solid #ffffff',
        borderRadius: '50%',
        animation: 'cart-sync-spin 1s linear infinite',
      });
      overlay.appendChild(spinner);
    }

    if (!document.getElementById('cart-sync-spin-style-page')) {
      const style = document.createElement('style');
      style.id = 'cart-sync-spin-style-page';
      style.textContent = `
      @keyframes cart-sync-spin {
        to { transform: rotate(360deg); }
      }
    `;
      document.head.appendChild(style);
    }
  }

  function hidePageSpinner() {
    const overlay = document.getElementById('cart-sync-overlay');
    if (overlay) overlay.remove();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // CLEAR SESSION
  // ───────────────────────────────────────────────────────────────────────────
  async function clearCartContents() {
    if (!sessionId) return;
    try {
      await syncCart({ items: [] }, 'cart_cleared');
      origin = '';
    } catch (err) {}

    await refreshCartUI();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // ENHANCED DEBOUNCED CART OPERATIONS
  // ───────────────────────────────────────────────────────────────────────────
  function debouncedCartOperation(operation) {
    cartOperationQueue.push(operation);

    if (syncDebounceTimer) {
      clearTimeout(syncDebounceTimer);
    }

    pendingSyncPromise = new Promise((resolve, reject) => {
      syncDebounceTimer = setTimeout(async () => {
        if (isProcessingQueue) {
          resolve();
          return;
        }

        isProcessingQueue = true;
        const operations = cartOperationQueue.splice(0, MAX_BATCH_SIZE);

        try {
          const latestOperation = operations[operations.length - 1];
          if (latestOperation) {
            await latestOperation();
          }
          resolve();
        } catch (error) {
          console.error('Debounced cart operation failed:', error);
          reject(error);
        } finally {
          isProcessingQueue = false;
          pendingSyncPromise = null;

          if (cartOperationQueue.length > 0) {
            debouncedCartOperation(() => {});
          }
        }
      }, DEBOUNCE_DELAY);
    });

    return pendingSyncPromise;
  }

  async function waitForPendingSync(buttonElement = null, timeout = 5000) {
    if (pendingSyncPromise) {
      let spinner = null;

      if (buttonElement) {
        spinner = document.createElement('span');
        spinner.classList.add('button-spinner');
        buttonElement.appendChild(spinner);
      }

      try {
        await Promise.race([
          pendingSyncPromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Sync timeout')), timeout)),
        ]);
      } catch (error) {
        if (error.message === 'Sync timeout') {
          console.warn('[Checkout] Cart sync timeout, proceeding with checkout');
        } else {
          console.warn('[Checkout] Cart sync failed, proceeding with checkout:', error);
        }
      } finally {
        if (spinner && spinner.parentNode) {
          spinner.parentNode.removeChild(spinner);
        }
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // DEBOUNCED SYNC + SYNC API CALL
  // ───────────────────────────────────────────────────────────────────────────
  async function scheduleSync() {
    try {
      const remoteRes = await originalFetch(
        `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}${customerEmail ? `&email=${encodeURIComponent(customerEmail)}` : ''}`,
      );

      const remoteResData = await remoteRes.json();
      const remoteItems = remoteResData?.items || [];
      const remoteEventType = remoteResData?.eventType;

      if (remoteEventType === 'cart_cleared') {
        await originalFetch('/cart/clear.js', { method: 'POST' });
        await syncCart({ items: [] }, 'cart_cleared');
        return;
      }

      const localCart = await originalFetch('/cart.js').then((r) => r.json());
      const localItems = localCart.items || [];

      const remoteMap = new Map(remoteItems.map((i) => [i.sku, i.quantity]));
      const localMap = new Map(localItems.map((i) => [i.sku, i.quantity]));
      const allSkus = new Set([...remoteMap.keys(), ...localMap.keys()]);
      const hasChanges = Array.from(allSkus).some((sku) => (remoteMap.get(sku) || 0) !== (localMap.get(sku) || 0));

      if (hasChanges) {
        const remoteFingerprint = remoteResData?.fingerprint || null;

        if (remoteFingerprint && remoteFingerprint !== fingerprint) {
          await replaceLocalCartWithRemote(remoteItems);
          return;
        }

        const preferMaxQuantities = localItems.map((li) => ({
          sku: li.sku,
          quantity: Math.max(li.quantity || 0, remoteMap.get(li.sku) || 0),
          handle: li.handle,
        }));
        const supplemented = [...preferMaxQuantities];
        for (const [sku, qty] of remoteMap.entries()) {
          if (!localMap.has(sku)) {
            supplemented.push({ sku, quantity: qty, handle: (remoteItems.find((r) => r.sku === sku) || {}).handle });
          }
        }
        await replaceLocalCartWithRemote(supplemented);
      } else {
      }
    } catch (err) {
      console.error('scheduleSync error:', err);
    }
  }

  async function syncCart(fullCart, event, preventSessionCreation = false) {
    try {
      if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
        if (preventSessionCreation) {
          return;
        }
        await ensureSessionExists();
      }

      if (!sessionId) {
        console.warn('Failed to create session, cannot sync cart');
        return;
      }

      const filteredItems = (fullCart.items || []).filter(
        (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
      );

      const slim = filteredItems.map((i) => ({
        handle: i.handle,
        sku: i.sku,
        quantity: i.quantity,
      }));

      let eventType = slim.length === 0 ? 'cart_cleared' : 'cart_updated';
      if (event) eventType = event;

      let localEntryStore = entryStore || host;
      let localEntryTs = entryTimestamp || Math.floor(Date.now() / 1000).toString();
      let localEntrySrc = entrySource || new URLSearchParams(location.search).toString() || document.referrer || '';

      if (!localEntryStore) {
        localEntryStore = host;
        entryStore = host;
      }
      if (!localEntryTs) {
        localEntryTs = Math.floor(Date.now() / 1000).toString();
        entryTimestamp = localEntryTs;
      }
      if (!localEntrySrc) {
        localEntrySrc = new URLSearchParams(location.search).toString() || document.referrer || '';
        entrySource = localEntrySrc;
      }

      let originStore = origin;
      if (!originStore && slim.length > 0) {
        originStore = window.Shopify.shop;
        origin = originStore;
      }

      const email = customerEmail || null;

      const payload = {
        session_id: sessionId,
        items: slim,
        email: email,
        entryStore: localEntryStore,
        entryTimestamp: localEntryTs,
        entrySource: localEntrySrc,
        fingerprint,
        event_type: eventType,
      };
      if (originStore) payload.originStore = originStore;

      const res = await originalFetch(`${API_BASE}/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        throw new Error('Authentication failed');
      }
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Sync failed: ${res.status} ${errorText}`);
      }

      try {
        const responseData = await res.json();

        if (responseData.migrated && responseData.migrated === true) {
          sessionId = email;
        } else if (responseData.prioritizeRemote && responseData.prioritizeRemote === true) {
          sessionId = email;
          await loadRemoteCartOnPageLoad();
        }
      } catch (parseError) {}
    } catch (error) {
      console.error('syncCart error:', error);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // REFRESH CART UI (COUNT + SECTIONS)
  // ───────────────────────────────────────────────────────────────────────────
  async function refreshCartUI() {
    const [countResult, miniCartResult, cartResult] = await Promise.allSettled([
      refreshCartCount(),
      refreshMiniCart(),
      refreshCart(),
    ]);

    hidePageSpinner();
    updateBreadcrumbs();
    window.loadReviewsIO();
    document.dispatchEvent(new Event('cart:upsell'));
    try {
      document
        .querySelectorAll('.cart__items.cart__items--disabled')
        .forEach((el) => el.classList.remove('cart__items--disabled'));
    } catch (_) {}
  }

  window.scheduleSync = scheduleSync;

  const variantCache = new Map();
  const VARIANT_CACHE_TTL = 60000;

  async function batchVariantLookup(items, store) {
    const handles = items.filter((item) => item.quantity > 0).map((item) => item.handle);
    if (handles.length === 0) return [];

    const cacheKey = `${store}-${handles.sort().join(',')}`;
    const cached = variantCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < VARIANT_CACHE_TTL) {
      return cached.data;
    }

    try {
      const response = await originalFetch(`${API_BASE}/variant/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handles, store }),
      });

      if (!response.ok) {
        throw new Error(`Batch variant lookup failed: ${response.status}`);
      }

      const { variants } = await response.json();

      variantCache.set(cacheKey, {
        data: variants,
        timestamp: Date.now(),
      });

      setTimeout(() => variantCache.delete(cacheKey), VARIANT_CACHE_TTL);

      return variants;
    } catch (error) {
      console.warn('Batch variant lookup failed, falling back to individual lookups:', error);

      const fallbackResults = await Promise.allSettled(
        handles.map(async (handle) => {
          try {
            const { id: gid } = await originalFetch(`${API_BASE}/variant`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ handle, store }),
            }).then((r) => r.json());

            return {
              handle,
              id: gid,
              available: true,
            };
          } catch (error) {
            return {
              handle,
              id: null,
              available: false,
              error: error.message,
            };
          }
        }),
      );

      return fallbackResults.map((result) =>
        result.status === 'fulfilled' ? result.value : { handle: '', id: null, available: false },
      );
    }
  }

  async function replaceLocalCartWithRemote(remoteItems) {
    try {
      const currentCart = await originalFetch('/cart.js').then((r) => r.json());
      const updates = {};

      for (const item of currentCart.items || []) {
        if (item.handle !== 'routeins' && item.sku !== 'ROUTEINS') {
          updates[item.variant_id] = 0;
        }
      }

      const variants = await batchVariantLookup(remoteItems, window.Shopify.shop);

      const variantMap = new Map();
      variants.forEach((variant) => {
        if (variant.id && variant.available) {
          const variantId = parseInt(variant.id.split('/').pop(), 10);
          variantMap.set(variant.handle, variantId);
        }
      });

      remoteItems.forEach(({ handle, quantity }) => {
        if (quantity > 0 && variantMap.has(handle)) {
          const variantId = variantMap.get(handle);
          updates[variantId] = quantity;
        }
      });

      if (Object.keys(updates).length > 0) {
        const updateResponse = await originalFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        if (updateResponse.ok) {
          await refreshCartUI();
        }
      } else {
        await refreshCartUI();
      }
    } catch (error) {
      console.error('Failed to replace local cart:', error);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // OVERRIDE window.fetch TO TRIGGER scheduleSync + UI REFRESH
  // ───────────────────────────────────────────────────────────────────────────
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';

    if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/clear')) {
      const p = originalFetch(input, init);
      if (url.includes('/cart/add') || url.includes('/cart/change')) {
        p.then(async (res) => {
          if (res.ok) {
            const localCart = await originalFetch('/cart.js').then((r) => r.json());

            if (sessionId && sessionId !== 'undefined' && sessionId !== 'null') {
              await syncCart(localCart, 'cart_updated');
            }

            if (url.includes('/cart/change')) {
              toggleItemTotalsSpinnerFromElement(lastChangeElement, true);
              try {
                await refreshCartUI();
              } finally {
                toggleItemTotalsSpinnerFromElement(lastChangeElement, false);
              }
            } else {
              await refreshCartUI();
            }

            return debouncedCartOperation(async () => {
              await scheduleSync();
            });
          }
        }).catch(console.error);
        return p;
      } else if (url.includes('/cart/clear')) {
        p.then(async (res) => {
          if (res.ok) {
            if (sessionId && sessionId !== 'undefined' && sessionId !== 'null') {
              await syncCart({ items: [] }, 'cart_cleared');
            }
            await scheduleSync();
            await refreshCartUI();
          }
        }).catch(console.error);
        return p;
      }
    }

    return originalFetch(input, init).catch((err) => {
      throw err;
    });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // CHECKOUT INTERCEPTION & REDIRECTION
  // ───────────────────────────────────────────────────────────────────────────
  function interceptCheckout() {
    let isProcessingCheckout = false;
    const checkoutSelectors = [
      'button[name="checkout"]',
      'button[id="checkout"]',
      '.cart__checkout-button',
      'a[href$="/checkout"]',
      'a[href="/checkout"]',
      '[data-checkout]',
      'input[name="checkout"]',
      'form[action="/cart"] button[type="submit"]',
      'button[form*="cart"]',
    ];

    async function onCheckoutAttempt(event) {
      if (isProcessingCheckout) {
        return;
      }

      const clicked = event.target.closest(checkoutSelectors.join(','));
      if (!clicked) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      isProcessingCheckout = true;

      try {
        await waitForPendingSync(clicked);

        // Ensure fingerprint_id is force-written and verified before any redirect
        try {
          await ensureFingerprintAttributeWithRetry(3, 150);
        } catch (_) {}

        const spinner = document.createElement('span');
        spinner.classList.add('button-spinner');
        clicked.appendChild(spinner);

        let currentEmail = customerEmail || null;

        const syncResponse = await originalFetch(
          `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}${currentEmail ? `&email=${encodeURIComponent(currentEmail)}` : ''}`,
        );

        if (syncResponse.ok) {
          const syncData = await syncResponse.json();
          const items = syncData.items || [];
          const remoteOriginStore = syncData.originStore;

          const currentHost = window.location.host;
          const originHost = remoteOriginStore;

          if (currentHost === originHost) {
            window.location.href = '/checkout';
            return;
          }

          if (items.length > 0) {
            const validItems = items.filter((item) => item.variantId && item.quantity > 0);

            if (validItems.length > 0) {
              const cartParams = validItems.map((item) => `${item.variantId}:${item.quantity}`).join(',');
              const urlParams = new URLSearchParams();
              try {
                await ensureValidFingerprint();
              } catch (_) {}
              urlParams.set('attributes[fingerprint_id]', fingerprint);

              const cartUrl = `https://${originHost}/cart/${cartParams}?${urlParams.toString()}`;

              window.location.href = cartUrl;
              return;
            } else {
              console.warn('[Checkout] No valid items with variant IDs found');
            }
          } else {
            console.warn('[Checkout] No items found in remote cart');
          }
        } else {
          console.warn('[Checkout] Failed to fetch remote cart data:', syncResponse.status);
        }

        let sessionResponse = null;
        try {
          if (currentEmail) {
            sessionResponse = await originalFetch(
              `${API_BASE}/cart/session?fingerprint=${encodeURIComponent(fingerprint)}&email=${encodeURIComponent(currentEmail)}`,
            );
          }
        } catch (_) {}

        if (!sessionResponse || !sessionResponse.ok) {
          sessionResponse = await originalFetch(
            `${API_BASE}/cart/session?fingerprint=${encodeURIComponent(fingerprint)}`,
          );
        }

        if (!sessionResponse.ok) throw new Error();
        const { session_id, originStore } = await sessionResponse.json();
        const originHost = (session_id?.originStore || originStore || window.Shopify.shop)
          .replace(/^https?:\/\//, '')
          .replace(/\/+$/, '');

        const checkoutResponse = await originalFetch(`${API_BASE}/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId, fingerprint, originStore: originHost }),
        });
        const { checkoutUrl, fallbackUrl } = await checkoutResponse.json();

        const finalUrl = checkoutUrl || fallbackUrl || `https://${originHost}/checkout`;
        window.location.href = finalUrl;
      } catch (error) {
        console.error('[Checkout] Error during checkout process:', error);
        window.location.href = '/checkout';
      } finally {
        setTimeout(() => {
          clicked.classList.remove('button--loading');
          isProcessingCheckout = false;
        }, 3000);
      }
    }

    document.addEventListener('click', onCheckoutAttempt, { capture: true });
    document.addEventListener('submit', onCheckoutAttempt, { capture: true });

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const text = target.textContent?.toLowerCase() || '';
        const href = target.href || '';

        if (text.includes('checkout') || href.includes('/checkout') || target.name === 'checkout') {
          onCheckoutAttempt(event);
        }
      },
      { capture: true },
    );
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Hook the interception IMMEDIATELY and on DOMContentLoaded
  // ───────────────────────────────────────────────────────────────────────────
  interceptCheckout();

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const checkoutElements = node.querySelectorAll?.(
              'button[name="checkout"], button[id="checkout"], .cart__checkout-button, a[href*="/checkout"]',
            );
            if (checkoutElements?.length > 0) {
              interceptCheckout();
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ───────────────────────────────────────────────────────────────────────────
  // ENHANCED FINGERPRINT GENERATION
  // ───────────────────────────────────────────────────────────────────────────
  async function generateFingerprint() {
    const comps = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.hardwareConcurrency || 'unknown',
      navigator.maxTouchPoints || 0,
    ].join('|');

    const data = new TextEncoder().encode(comps);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const fingerprint = Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return fingerprint;
  }

  let fingerprint = '';

  // ───────────────────────────────────────────────────────────────────────────
  // INITIALIZE FINGERPRINT ONLY (NO SESSION CREATION)
  // ───────────────────────────────────────────────────────────────────────────
  async function initializeFingerprint() {
    try {
      const storedFingerprint = localStorage.getItem('fingerprint');
      if (
        storedFingerprint &&
        storedFingerprint !== 'undefined' &&
        storedFingerprint !== 'null' &&
        storedFingerprint.length === 64 &&
        /^[a-f0-9]{64}$/.test(storedFingerprint)
      ) {
        fingerprint = storedFingerprint;
        return;
      }

      fingerprint = await generateFingerprint();
      localStorage.setItem('fingerprint', fingerprint);
    } catch (e) {
      console.warn('Fingerprint initialization failed, generating new one:', e);
      try {
        fingerprint = await generateFingerprint();
        localStorage.setItem('fingerprint', fingerprint);
      } catch (fallbackError) {
        fingerprint = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('fingerprint', fingerprint);
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // ENSURE VALID FINGERPRINT + CART ATTRIBUTE
  // ───────────────────────────────────────────────────────────────────────────
  function isFingerprintStringValid(value) {
    if (!value || value === 'undefined' || value === 'null') return false;
    if (typeof value === 'string' && value.startsWith('fallback_')) return true;
    return typeof value === 'string' && value.length === 64 && /^[a-f0-9]{64}$/.test(value);
  }

  async function ensureValidFingerprint() {
    try {
      if (!isFingerprintStringValid(fingerprint)) {
        const stored = localStorage.getItem('fingerprint');
        if (isFingerprintStringValid(stored)) {
          fingerprint = stored;
        } else {
          try {
            fingerprint = await generateFingerprint();
            localStorage.setItem('fingerprint', fingerprint);
          } catch (_) {
            fingerprint = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            try {
              localStorage.setItem('fingerprint', fingerprint);
            } catch (_) {}
          }
        }
      }
    } catch (_) {
      if (!isFingerprintStringValid(fingerprint)) {
        fingerprint = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        try {
          localStorage.setItem('fingerprint', fingerprint);
        } catch (_) {}
      }
    }
    return fingerprint;
  }

  async function ensureFingerprintAttribute() {
    try {
      await ensureValidFingerprint();
      const cart = await originalFetch('/cart.js').then((r) => r.json());
      const current = cart?.attributes?.fingerprint_id;
      if (current !== fingerprint) {
        await originalFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { fingerprint_id: fingerprint } }),
        });
      }
    } catch (error) {
      console.warn('ensureFingerprintAttribute failed:', error);
    }
  }

  async function ensureFingerprintAttributeWithRetry(maxAttempts = 3, delayMs = 150) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await ensureFingerprintAttribute();
        const verifyCart = await originalFetch('/cart.js').then((r) => r.json());
        const verified = verifyCart?.attributes?.fingerprint_id;
        if (verified === fingerprint) {
          return true;
        }
      } catch (error) {
        // fall through to retry
      }
      if (attempt < maxAttempts) {
        try {
          await new Promise((r) => setTimeout(r, delayMs));
        } catch (_) {}
      }
    }
    console.warn('ensureFingerprintAttributeWithRetry: failed to verify fingerprint_id after retries');
    return false;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // HANDLE BUY AGAIN FLOW
  // ───────────────────────────────────────────────────────────────────────────
  async function handleBuyAgainFlow() {
    if (window._buyAgainProcessing) {
      return;
    }

    window._buyAgainProcessing = true;

    try {
      const ts = Date.now();
      const lock = sessionStorage.getItem('buyAgainLock');
      if (lock) {
        const parsed = JSON.parse(lock);
        if (parsed?.ts && ts - parsed.ts < 10000) {
          return;
        }
      }
      sessionStorage.setItem('buyAgainLock', JSON.stringify({ ts }));

      let localCart;
      localCart = await originalFetch('/cart.js').then((r) => r.json());

      const isRoute = (i) => {
        const handle = (i?.handle || i?.product_handle || '').toLowerCase();
        const sku = (i?.sku || '').toUpperCase();
        return handle === 'routeins' || sku === 'ROUTEINS';
      };
      const localItems = (localCart.items || [])
        .filter((i) => i?.sku && !isRoute(i))
        .map((i) => ({ sku: i.sku, handle: i.handle || i.product_handle || null, quantity: i.quantity || 0 }));
      if (localItems.length === 0) {
        return;
      }

      if (!sessionId) await ensureSessionExists();
      if (!sessionId || sessionId === 'undefined' || sessionId === 'null') {
        sessionId = fingerprint;
        const now = Math.floor(Date.now() / 1000).toString();
        const utm = new URLSearchParams(location.search).toString() || document.referrer || '';
        origin = host;
        entryStore = host;
        entryTimestamp = now;
        entrySource = utm;
      }
      let remoteItems = [];
      const res = await originalFetch(
        `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}${customerEmail ? `&email=${encodeURIComponent(customerEmail)}` : ''}`,
      );
      if (res.ok) {
        const data = await res.json();
        remoteItems = (data?.items || [])
          .filter((i) => i?.sku && !isRoute(i))
          .map((i) => ({ sku: i.sku, handle: i.handle || i.product_handle || null, quantity: i.quantity || 0 }));
      }

      const map = new Map();
      const addAll = (arr, source) => {
        for (const i of arr) {
          if (!i?.sku) continue;
          const prev = map.get(i.sku) || { sku: i.sku, handle: i.handle || null, quantity: 0 };
          const nextQty = (prev.quantity || 0) + (i.quantity || 0);
          map.set(i.sku, { sku: i.sku, handle: prev.handle || i.handle || null, quantity: nextQty, source });
        }
      };
      addAll(remoteItems, 'remote');
      addAll(localItems, 'local');
      const merged = Array.from(map.values()).map((i) => ({ handle: i.handle, sku: i.sku, quantity: i.quantity }));
      if (remoteItems.length > 0) {
        await syncCart({ items: merged }, 'cart_updated');
        await replaceLocalCartWithRemote(merged);
        try {
          window.skipRemoteReplaceOnce = true;
        } catch (_) {}
        try {
          sessionStorage.setItem('skipInitialMergeOnce', '1');
        } catch (_) {}
        await refreshCartUI();
        return;
      } else {
        await syncCart({ items: localItems }, 'cart_updated');
        try {
          window.skipRemoteReplaceOnce = true;
        } catch (_) {}
        try {
          sessionStorage.setItem('skipInitialMergeOnce', '1');
        } catch (_) {}
        await refreshCartUI();
        return;
      }
    } catch (err) {
      console.error('[BuyAgain] unexpected error:', err);
    } finally {
      window._buyAgainProcessing = false;
      try {
        sessionStorage.removeItem('buyAgainLock');
      } catch (_) {}
    }
  }

  async function loadRemoteCartOnPageLoad() {
    if (location.pathname === '/cart') showPageSpinner();

    const urlParams = new URLSearchParams(location.search);
    const cartLinkId = urlParams.get('cart_link_id');

    try {
      if (cartLinkId) {
        if (window._buyAgainHandled === true) {
          return;
        }
        window._buyAgainHandled = true;
        try {
          await new Promise((r) => setTimeout(r, 1500));
        } catch (_) {}
        try {
          const localCart = await originalFetch('/cart.js').then((r) => r.json());
          const items = Array.isArray(localCart?.items) ? localCart.items : [];
          const hasRoute = items.some((i) => {
            const handle = (i?.handle || i?.product_handle || '').toLowerCase();
            const sku = (i?.sku || '').toUpperCase();
            return handle === 'routeins' || sku === 'ROUTEINS';
          });
          if (!hasRoute) {
            const url = new URL(window.location.href);
            url.searchParams.delete('cart_link_id');
            window.history.replaceState({}, '', url.toString());
          }
        } catch (e) {
          console.warn(e);
        }

        await handleBuyAgainFlow();
        return;
      } else {
        await loadRemoteCartOnPageLoadNormal();
      }
    } catch (error) {
      await refreshCartUI();
    } finally {
      hidePageSpinner();
      window.loadReviewsIO();
    }
  }

  async function loadRemoteCartOnPageLoadNormal() {
    try {
      let currentEmail = customerEmail || null;

      if (!sessionId) {
        await ensureSessionExists();
      }

      let hasRemoteData = false;
      let remoteItems = [];

      try {
        const [remoteRes, migrationRes] = await Promise.allSettled([
          originalFetch(
            `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}` +
              `${currentEmail ? `&email=${encodeURIComponent(currentEmail)}` : ''}`,
          ),
          currentEmail
            ? (async () => {
                try {
                  const fingerprintCheck = await originalFetch(
                    `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`,
                  );
                  if (fingerprintCheck.ok) {
                    const fingerprintData = await fingerprintCheck.json();
                    if (fingerprintData?.eventType === 'cart_cleared') {
                      return null;
                    }
                  }
                  return originalFetch(`${API_BASE}/migrate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      fingerprint: fingerprint,
                      email: currentEmail,
                    }),
                  });
                } catch (error) {
                  console.warn('[Cart Sync] Failed to check fingerprint session before migration:', error);
                  return originalFetch(`${API_BASE}/migrate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      fingerprint: fingerprint,
                      email: currentEmail,
                    }),
                  });
                }
              })()
            : Promise.resolve(null),
        ]);

        if (remoteRes.status === 'fulfilled') {
          const remote = remoteRes.value;

          if (remote.status === 404) {
            const localCart = await originalFetch('/cart.js').then((r) => r.json());
            const localItems = localCart.items || [];

            if (localItems.length > 0) {
              if (!window.skipRemoteReplaceOnce) {
                await originalFetch('/cart/clear.js', { method: 'POST' });
                await refreshCartUI();
                hidePageSpinner();
                window.loadReviewsIO();
                return;
              }
              window.skipRemoteReplaceOnce = false;
            }

            if (currentEmail) {
              hidePageSpinner();
              window.loadReviewsIO();
              return;
            }

            if (!sessionId) {
              sessionId = fingerprint;
              const now = Math.floor(Date.now() / 1000).toString();
              const utm = new URLSearchParams(location.search).toString() || document.referrer || '';

              origin = host;
              entryStore = host;
              entryTimestamp = now;
              entrySource = utm;
            } else {
            }

            hidePageSpinner();
            window.loadReviewsIO();
            return;
          }

          if (remote.ok) {
            const remoteData = await remote.json();
            remoteItems = (remoteData?.items || []).filter(
              (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
            );
            hasRemoteData = true;

            if (remoteData?.originStore) origin = remoteData?.originStore;
            if (remoteData?.entryStore) entryStore = remoteData?.entryStore;
            if (remoteData?.entryTimestamp) entryTimestamp = remoteData?.entryTimestamp;
            if (remoteData?.entrySource) entrySource = remoteData?.entrySource;

            if (!sessionId) {
              sessionId = fingerprint;
            }

            const originStoreValue = remoteData?.originStore || window.Shopify.shop;
            const mappedOrigin = (() => {
              switch (originStoreValue) {
                case STORE_B:
                  return 'CBC';
                case STORE_A:
                  return 'MK';
                default:
                  return originStoreValue;
              }
            })();
            localStorage.setItem('originStore', mappedOrigin);

            if (remoteData?.eventType === 'cart_cleared') {
              if (!window.skipRemoteReplaceOnce) {
                await originalFetch('/cart/clear.js', { method: 'POST' });
                await refreshCartUI();
                hidePageSpinner();
                window.loadReviewsIO();
                return;
              }
              window.skipRemoteReplaceOnce = false;
            }
          }
        }

        if (migrationRes.status === 'fulfilled' && migrationRes.value) {
          const migration = migrationRes.value;
          if (migration.ok) {
            const migrationData = await migration.json();

            if (migrationData.migrated) {
              try {
                const originalSessionCheck = await originalFetch(
                  `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`,
                );

                if (originalSessionCheck.ok) {
                  const originalSessionData = await originalSessionCheck.json();

                  if (originalSessionData?.eventType === 'cart_cleared') {
                    await originalFetch('/cart/clear.js', { method: 'POST' });
                    await refreshCartUI();
                    hidePageSpinner();
                    window.loadReviewsIO();
                    return;
                  }
                }
              } catch (error) {
                console.warn('[Cart Sync] Failed to check original session before migration:', error);
              }

              sessionId = currentEmail;

              if (migrationData.items && migrationData.items.length > 0) {
                remoteItems = migrationData.items.filter(
                  (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
                );
                hasRemoteData = true;

                if (migrationData.originStore) origin = migrationData.originStore;
                if (migrationData.entryStore) entryStore = migrationData.entryStore;
                if (migrationData.entryTimestamp) entryTimestamp = migrationData.entryTimestamp;
                if (migrationData.entrySource) entrySource = migrationData.entrySource;

                const originStoreValue = migrationData.originStore || window.Shopify.shop;
                const mappedOrigin = (() => {
                  switch (originStoreValue) {
                    case STORE_B:
                      return 'CBC';
                    case STORE_A:
                      return 'MK';
                    default:
                      return originStoreValue;
                  }
                })();
                localStorage.setItem('originStore', mappedOrigin);
              } else if (migrationData.eventType === 'cart_cleared') {
                await originalFetch('/cart/clear.js', { method: 'POST' });
                await refreshCartUI();
                hidePageSpinner();
                window.loadReviewsIO();
                return;
              } else {
                hasRemoteData = true;
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to check remote session or migration:', error);
      }

      if (currentEmail && hasRemoteData && remoteItems.length > 0) {
        try {
          let needsMerge = false;

          if (window.needsSessionMerge !== undefined) {
            needsMerge = window.needsSessionMerge;
          } else {
            const remoteFingerprint = remoteItems[0]?.fingerprint || null;
            needsMerge = Boolean(remoteFingerprint && remoteFingerprint !== fingerprint);
          }

          try {
            const skipOnce = sessionStorage.getItem('skipInitialMergeOnce') === '1';
            if (skipOnce) {
              needsMerge = false;
              sessionStorage.removeItem('skipInitialMergeOnce');
            }
          } catch (_) {}

          if (needsMerge) {
            const localCart = await originalFetch('/cart.js').then((r) => r.json());
            const localItems = localCart.items || [];

            const mergedItems = [];
            const itemMap = new Map();

            remoteItems.forEach((item) => {
              if (item.sku) {
                itemMap.set(item.sku, { ...item, source: 'remote' });
              }
            });

            localItems.forEach((item) => {
              if (item.sku && itemMap.has(item.sku)) {
                const remoteItem = itemMap.get(item.sku);
                const mergedQuantity = remoteItem.quantity + item.quantity;
                itemMap.set(item.sku, {
                  ...remoteItem,
                  quantity: mergedQuantity,
                  source: 'merged',
                });
              } else if (item.sku) {
                itemMap.set(item.sku, { ...item, source: 'local' });
              }
            });

            mergedItems.push(...itemMap.values());

            if (mergedItems.length > 0) {
              await replaceLocalCartWithRemote(mergedItems);

              await syncCart({ items: mergedItems }, 'cart_updated');
            } else {
              await originalFetch('/cart/clear.js', { method: 'POST' });
              await refreshCartUI();
            }
          } else {
            if (window.shouldUpdateLocalCart && window.emailSessionItems) {
              await replaceLocalCartWithRemote(window.emailSessionItems);

              window.shouldUpdateLocalCart = false;
              window.emailSessionItems = null;
            } else if (window.shouldClearLocalCart) {
              await originalFetch('/cart/clear.js', { method: 'POST' });
              await refreshCartUI();
              window.shouldClearLocalCart = false;
            } else {
              await replaceLocalCartWithRemote(remoteItems);
            }
          }
        } catch (error) {
          console.error('Failed to merge local and remote cart items:', error);
          await replaceLocalCartWithRemote(remoteItems);
        }
      } else if (currentEmail && hasRemoteData && remoteItems.length === 0) {
        try {
          const remoteSessionCheck = await originalFetch(`${API_BASE}/sync?email=${encodeURIComponent(currentEmail)}`);

          if (remoteSessionCheck.ok) {
            const remoteSessionData = await remoteSessionCheck.json();

            if (remoteSessionData?.eventType === 'cart_cleared') {
              await originalFetch('/cart/clear.js', { method: 'POST' });
              await refreshCartUI();
              return;
            }
          }

          await refreshCartUI();
        } catch (error) {
          console.error('[Cart Sync] Failed to check remote session for cart_cleared:', error);
          await refreshCartUI();
        }
      } else if (currentEmail && !hasRemoteData && window.shouldCheckCartCleared) {
        try {
          const [fingerprintSessionCheck, emailSessionCheck] = await Promise.allSettled([
            originalFetch(`${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`),
            originalFetch(`${API_BASE}/sync?email=${encodeURIComponent(currentEmail)}`),
          ]);

          let shouldClearLocalCart = false;

          if (fingerprintSessionCheck.status === 'fulfilled' && fingerprintSessionCheck.value.ok) {
            const fingerprintData = await fingerprintSessionCheck.value.json();
            if (fingerprintData?.eventType === 'cart_cleared') {
              shouldClearLocalCart = true;
            }
          }

          if (emailSessionCheck.status === 'fulfilled' && emailSessionCheck.value.ok) {
            const emailData = await emailSessionCheck.value.json();
            if (emailData?.eventType === 'cart_cleared') {
              shouldClearLocalCart = true;
            }
          }

          if (shouldClearLocalCart) {
            await originalFetch('/cart/clear.js', { method: 'POST' });
            await refreshCartUI();
          } else {
            await refreshCartUI();
          }

          window.shouldCheckCartCleared = false;
        } catch (error) {
          console.error('[Cart Sync] Failed to check sessions for cart_cleared:', error);
          await refreshCartUI();
        }
      } else if (hasRemoteData) {
        await replaceLocalCartWithRemote(remoteItems);
      } else {
        await refreshCartUI();
      }
    } catch (error) {
      console.error('Page load cart check failed:', error);
      await refreshCartUI();
    } finally {
      hidePageSpinner();
      window.loadReviewsIO();
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // LAZY SESSION CREATION WITH LOGIN MIGRATION SUPPORT
  // ───────────────────────────────────────────────────────────────────────────
  async function ensureSessionExists() {
    let currentEmail = customerEmail || null;

    if (currentEmail && sessionId === currentEmail) {
      return;
    }

    if (currentEmail && sessionId === fingerprint) {
      try {
        if (window.needsSessionMerge === true) {
          return;
        }

        const emailSessionCheck = await originalFetch(`${API_BASE}/sync?email=${encodeURIComponent(currentEmail)}`);

        if (emailSessionCheck.ok) {
          const emailSessionData = await emailSessionCheck.json();
          const emailSessionItems = emailSessionData?.items || [];

          if (emailSessionItems.length > 0) {
            const fingerprintSessionCheck = await originalFetch(
              `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`,
            );
            let fingerprintItems = [];

            if (fingerprintSessionCheck.ok) {
              const fingerprintData = await fingerprintSessionCheck.json();
              fingerprintItems = (fingerprintData?.items || []).filter(
                (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
              );
            }

            if (fingerprintItems.length > 0) {
              const mergedItems = [];
              const itemMap = new Map();

              fingerprintItems.forEach((item) => {
                if (item.sku) {
                  itemMap.set(item.sku, { ...item, source: 'fingerprint' });
                }
              });

              emailSessionItems.forEach((item) => {
                if (item.sku && itemMap.has(item.sku)) {
                  const fingerprintItem = itemMap.get(item.sku);
                  const mergedQuantity = fingerprintItem.quantity + item.quantity;
                  itemMap.set(item.sku, {
                    ...fingerprintItem,
                    quantity: mergedQuantity,
                    source: 'merged',
                  });
                } else if (item.sku) {
                  itemMap.set(item.sku, { ...item, source: 'email' });
                }
              });

              mergedItems.push(...itemMap.values());

              if (mergedItems.length > 0) {
                await syncCart({ items: mergedItems }, 'cart_updated');

                sessionId = currentEmail;

                setTimeout(async () => {
                  try {
                    await replaceLocalCartWithRemote(mergedItems);
                  } catch (error) {
                    console.error('[Cart Sync] Failed to update local cart after session merge:', error);
                  }
                }, 100);
                return;
              }
            } else {
              sessionId = currentEmail;
              return;
            }
          }
        }

        const migrationRes = await originalFetch(`${API_BASE}/migrate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fingerprint: fingerprint,
            email: currentEmail,
          }),
        });

        if (migrationRes.ok) {
          const migrationData = await migrationRes.json();

          if (migrationData.migrated) {
            sessionId = currentEmail;

            if (migrationData.items && migrationData.items.length > 0) {
              const remoteItems = migrationData.items.filter(
                (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
              );
              if (remoteItems.length > 0) {
                setTimeout(async () => {
                  try {
                    const localCart = await originalFetch('/cart.js').then((r) => r.json());
                    const localItems = localCart.items || [];

                    if (localItems.length > 0) {
                      const remoteFingerprint = remoteItems[0]?.fingerprint || null;
                      const needsMerge = remoteFingerprint && remoteFingerprint !== fingerprint;

                      if (needsMerge) {
                        const mergedItems = [];
                        const itemMap = new Map();

                        remoteItems.forEach((item) => {
                          if (item.sku) {
                            itemMap.set(item.sku, { ...item, source: 'remote' });
                          }
                        });

                        localItems.forEach((item) => {
                          if (item.sku && itemMap.has(item.sku)) {
                            const remoteItem = itemMap.get(item.sku);
                            const mergedQuantity = remoteItem.quantity + item.quantity;
                            itemMap.set(item.sku, {
                              ...remoteItem,
                              quantity: mergedQuantity,
                              source: 'merged',
                            });
                          } else if (item.sku) {
                            itemMap.set(item.sku, { ...item, source: 'local' });
                          }
                        });

                        mergedItems.push(...itemMap.values());

                        if (mergedItems.length > 0) {
                          await replaceLocalCartWithRemote(mergedItems);

                          await syncCart({ items: mergedItems }, 'cart_updated');
                        }
                      } else {
                        await replaceLocalCartWithRemote(remoteItems);
                      }
                    } else {
                      await replaceLocalCartWithRemote(remoteItems);
                    }

                    await scheduleSync();
                  } catch (error) {
                    console.error('[Cart Sync] Failed to update cart after migration:', error);
                    await replaceLocalCartWithRemote(remoteItems);
                  }
                }, 100);
              }
            }
            return;
          }
        }
      } catch (error) {
        console.warn('ensureSessionExists: Migration failed:', error);
      }
    }

    if (currentEmail && !sessionId) {
      try {
        const remoteSessionCheck = await originalFetch(`${API_BASE}/sync?email=${encodeURIComponent(currentEmail)}`);

        if (remoteSessionCheck.ok) {
          const remoteSessionData = await remoteSessionCheck.json();

          if (
            remoteSessionData?.items &&
            remoteSessionData.items.length > 0 &&
            remoteSessionData?.eventType !== 'cart_cleared'
          ) {
            sessionId = currentEmail;
          } else if (remoteSessionData?.eventType === 'cart_cleared') {
            return;
          } else {
            return;
          }
        } else {
          return;
        }
      } catch (error) {
        console.warn('[Cart Sync] Failed to check remote session before creating new session:', error);
        return;
      }
      return;
    }

    if (sessionId && sessionId !== 'undefined' && sessionId !== 'null') {
      return;
    }

    try {
      let sessionResp = null;
      let emailCheck = customerEmail || null;

      if (emailCheck) {
        try {
          sessionResp = await originalFetch(
            `${API_BASE}/cart/session?fingerprint=${encodeURIComponent(fingerprint)}&email=${encodeURIComponent(emailCheck)}`,
          );
        } catch (error) {
          console.warn('[Cart Sync] Failed to check combined session:', error);
        }
      }

      if (!sessionResp || !sessionResp.ok) {
        sessionResp = await originalFetch(`${API_BASE}/cart/session?fingerprint=${encodeURIComponent(fingerprint)}`);
      }

      if (sessionResp.ok) {
        const data = await sessionResp.json();
        const recoveredSessionId = data?.session_id?.sessionId;

        if (recoveredSessionId && recoveredSessionId !== 'undefined' && recoveredSessionId !== 'null') {
          sessionId = recoveredSessionId;

          if (data.session_id.originStore) origin = data.session_id.originStore;
          if (data.session_id.entryStore) entryStore = data.session_id.entryStore;
          if (data.session_id.entryTimestamp) entryTimestamp = data.session_id.entryTimestamp;
          if (data.session_id.entrySource) entrySource = data.session_id.entrySource;

          return;
        }
      } else if (sessionResp.status === 404) {
        try {
          const existingSessionCheck = await originalFetch(
            `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`,
          );
          if (existingSessionCheck.ok) {
            const existingSessionData = await existingSessionCheck.json();
            if (existingSessionData?.items && existingSessionData.items.length > 0) {
              sessionId = fingerprint;
              return;
            }
          }
        } catch (error) {
          console.warn('[Cart Sync] Failed to check for existing fingerprint session:', error);
        }

        if (!emailCheck) {
          sessionId = fingerprint;

          const now = Math.floor(Date.now() / 1000).toString();
          const utm = new URLSearchParams(location.search).toString() || document.referrer || '';

          origin = host;
          entryStore = host;
          entryTimestamp = now;
          entrySource = utm;

          // Removed non-checkout fingerprint attribute writes
          return;
        }
      }
    } catch (err) {
      console.warn('Session check failed:', err);
    }

    sessionId = null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // MAIN INITIALIZATION - READ-ONLY ON PAGE LOAD
  // ───────────────────────────────────────────────────────────────────────────
  (async () => {
    await initializeFingerprint();

    if (customerEmail) {
      try {
        const [fingerprintRes, emailRes] = await Promise.allSettled([
          originalFetch(`${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`),
          originalFetch(`${API_BASE}/sync?email=${encodeURIComponent(customerEmail)}`),
        ]);

        let fingerprintItems = [];
        let emailItems = [];
        let fingerprintFingerprint = null;

        if (fingerprintRes.status === 'fulfilled' && fingerprintRes.value.ok) {
          const fingerprintData = await fingerprintRes.value.json();
          fingerprintItems = (fingerprintData?.items || []).filter(
            (item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS',
          );
          fingerprintFingerprint = fingerprintData?.fingerprint || null;
        }

        if (emailRes.status === 'fulfilled' && emailRes.value.ok) {
          const emailData = await emailRes.value.json();
          emailItems = (emailData?.items || []).filter((item) => item.handle !== 'routeins' && item.sku !== 'ROUTEINS');
        }

        if (
          fingerprintItems.length > 0 &&
          emailItems.length > 0 &&
          fingerprintFingerprint &&
          fingerprintFingerprint !== fingerprint
        ) {
          window.needsSessionMerge = true;
        } else if (
          fingerprintItems.length > 0 &&
          emailItems.length > 0 &&
          fingerprintFingerprint &&
          fingerprintFingerprint === fingerprint
        ) {
          window.needsSessionMerge = false;
        } else if (fingerprintItems.length > 0 && emailItems.length > 0) {
          const fingerprintSkus = new Set(fingerprintItems.map((item) => item.sku));
          const emailSkus = new Set(emailItems.map((item) => item.sku));
          const hasDifferentItems =
            fingerprintSkus.size !== emailSkus.size || ![...fingerprintSkus].every((sku) => emailSkus.has(sku));

          if (hasDifferentItems) {
            window.needsSessionMerge = true;
          } else {
            window.needsSessionMerge = false;
          }
        } else if (fingerprintItems.length === 0 && emailItems.length > 0) {
          try {
            const fingerprintSessionCheck = await originalFetch(
              `${API_BASE}/sync?fingerprint=${encodeURIComponent(fingerprint)}`,
            );

            if (fingerprintSessionCheck.ok) {
              const fingerprintSessionData = await fingerprintSessionCheck.json();

              if (fingerprintSessionData?.eventType === 'cart_cleared') {
                window.needsSessionMerge = false;
                window.shouldClearLocalCart = true;
              } else {
                window.needsSessionMerge = false;
                window.shouldUpdateLocalCart = true;
                window.emailSessionItems = emailItems;
              }
            } else {
              window.needsSessionMerge = false;
              window.shouldUpdateLocalCart = true;
              window.emailSessionItems = emailItems;
            }
          } catch (error) {
            console.warn('[Cart Sync] Failed to check fingerprint session status:', error);
            window.needsSessionMerge = false;
            window.shouldUpdateLocalCart = true;
            window.emailSessionItems = emailItems;
          }
        } else if (fingerprintItems.length === 0 && emailItems.length === 0) {
          window.needsSessionMerge = false;
          window.shouldCheckCartCleared = true;
        } else if (fingerprintItems.length > 0 && emailItems.length === 0) {
          window.needsSessionMerge = false;
        }
      } catch (error) {
        console.warn('[Cart Sync] Failed to check sessions before initialization:', error);
      }
    }

    await loadRemoteCartOnPageLoad();
  })();

  // ───────────────────────────────────────────────────────────────────────────
  // HANDLE REMOVE ITEM CLICKS
  // ───────────────────────────────────────────────────────────────────────────
  document.addEventListener(
    'click',
    async (e) => {
      const removeLink = e.target.closest('.c-button__remove-item');
      if (!removeLink) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      const cartRemoveButton = removeLink.closest('cart-remove-button');
      const line = cartRemoveButton?.dataset.index;
      if (!line) return;

      const cartItem = removeLink.closest('.c-cart-item');
      const variantId = cartItem?.dataset.productId;
      if (!variantId) return;

      const itemSelectors = [`#CartItem-${line} .loading__spinner`, `#CartDrawer-Item-${line} .loading__spinner`].join(
        ',',
      );
      document.querySelectorAll(itemSelectors).forEach((s) => s.classList.remove('hidden'));

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '0');

        const res = await originalFetch('/cart/change.js', {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) throw new Error(`Remove failed (${res.status})`);

        const cart = await res.json();

        if (cart.items.length === 0) {
          await clearCartContents();
        } else {
          if (sessionId && sessionId !== 'undefined' && sessionId !== 'null') {
            await syncCart(cart, 'cart_updated');
          } else {
          }
          await refreshCartUI();
        }
      } catch (err) {
        console.error(err);
      } finally {
        document.querySelectorAll(itemSelectors).forEach((s) => s.classList.add('hidden'));
      }
    },
    { capture: true },
  );

  // ───────────────────────────────────────────────────────────────────────────
  // UPDATE ACTIVE STORE LOGO ON UTILITY BAR
  // ───────────────────────────────────────────────────────────────────────────
  const storeALinks = document.querySelectorAll('#utility-bar-link__mackenzie');
  const storeBLinks = document.querySelectorAll('#utility-bar-link__chesapeake');

  if (host.includes(STORE_A)) {
    storeALinks.forEach((el) => {
      el.classList.add('utility-bar__logo--active');
      el.classList.remove('utility-bar__logo');
    });
    storeBLinks.forEach((el) => {
      el.classList.remove('utility-bar__logo--active');
      el.classList.add('utility-bar__logo');
    });
  } else if (host.includes(STORE_B)) {
    storeBLinks.forEach((el) => {
      el.classList.add('utility-bar__logo--active');
      el.classList.remove('utility-bar__logo');
    });
    storeALinks.forEach((el) => {
      el.classList.remove('utility-bar__logo--active');
      el.classList.add('utility-bar__logo');
    });
  }
});
